import React, { useState, useEffect } from "react";
import api from "../../../../api";
import { Card, Row, Col, Badge, Form } from "react-bootstrap";
import { useParams } from "react-router-dom";

const DPRTab = () => {
 const { id: projectId } = useParams();
 const [dprs, setDprs] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState("");
 const [filterDate, setFilterDate] = useState("today");

 // Convert filter to a date range start
 const getDateQuery = () => {
 const today = new Date();

 if (filterDate === "today") return today.toISOString().slice(0, 10);

 if (filterDate === "7days") {
 const d = new Date(today);
 d.setDate(today.getDate() - 7);
 return d.toISOString().slice(0, 10);
 }

 if (filterDate === "30days") {
 const d = new Date(today);
 d.setDate(today.getDate() - 30);
 return d.toISOString().slice(0, 10);
 }

 return filterDate;
 };

 useEffect(() => {
 const fetchDPRs = async () => {
 try {
 const fromDate = getDateQuery();

 const response = await api.get("/dpr", {
 params: { workOrder: projectId, fromDate },
 });

 if (response.data.success) {
 setDprs(response.data.data || []);
 } else setError("Unable to load DPR data.");
 } catch (err) {
 setError("Error fetching DPR data.");
 } finally {
 setLoading(false);
 }
 };

 if (projectId) fetchDPRs();
 }, [projectId, filterDate]);

 if (loading) return <p>Loading DPR data...</p>;
 if (error) return <p className="text-danger">{error}</p>;

 return (
 <>
 {/* FILTER */}
 <Card className="shadow-sm border-0 mb-4">
 <Card.Body>
 <h5 className="fw-bold mb-3">Filter DPR Records</h5>

 <div className="d-flex gap-3 align-items-center">
 <Form.Select
 className="form-control"
 value={filterDate}
 onChange={(e) => setFilterDate(e.target.value)}
 style={{ width: "220px" }}
 >
 <option value="today">Today</option>
 <option value="7days">Last 7 Days</option>
 <option value="30days">Last 30 Days</option>
 <option value="">Custom Date</option>
 </Form.Select>

 {filterDate === "" && (
 <Form.Control
 type="date"
 onChange={(e) => setFilterDate(e.target.value)}
 style={{ width: "220px" }}
 />
 )}
 </div>
 </Card.Body>
 </Card>

 {/* DPR CARDS */}
 {dprs.length === 0 ? (
 <p className="text-muted">No DPR records found for this project.</p>
 ) : (
 dprs.map((dpr) => (
 <Card
 key={dpr._id}
 className="shadow-sm border-0 mb-4"
 style={{ borderRadius: "14px" }}
 >
 <Card.Body>
 {/* HEADER */}
 <div className="d-flex justify-content-between align-items-center mb-3">
 <div>
 <h5 className="fw-bold mb-1">DPR – {dpr.dprNumber}</h5>
 <Badge bg="primary" className="rounded-pill">
 {new Date(dpr.date).toDateString()}
 </Badge>
 </div>

 <Badge
 className="rounded-pill"
 bg={
 dpr.status === "Approved"
 ? "success"
 : dpr.status === "Submitted"
 ? "primary"
 : "secondary"
 }
 >
 {dpr.status}
 </Badge>
 </div>

 <Row className="g-4">
 {/* ACTIVITIES */}
 <Col md={6}>
 <Card className="border-0 shadow-sm h-100">
 <Card.Body>
 <h6 className="fw-bold mb-3">Activities Completed</h6>

 {dpr.activitiesCompleted?.length === 0 ? (
 <p className="text-muted">No activities recorded.</p>
 ) : (
 dpr.activitiesCompleted.map((a, idx) => (
 <div
 key={idx}
 className="p-3 mb-3 border rounded"
 style={{ background: "#fafafa" }}
 >
 <div className="d-flex justify-content-between">
 <strong>{a.activity}</strong>
 <Badge
 className="rounded-pill"
 bg={
 a.status === "Completed"
 ? "success"
 : a.status === "In Progress"
 ? "warning"
 : "secondary"
 }
 >
 {a.status}
 </Badge>
 </div>
 <p className="small text-muted mb-1">
 {a.description}
 </p>
 <p className="mb-0">
 Quantity:{" "}
 <strong>
 {a.quantityCompleted} {a.unit}
 </strong>
 </p>
 </div>
 ))
 )}
 </Card.Body>
 </Card>
 </Col>

 {/* MANPOWER */}
 <Col md={6}>
 <Card className="border-0 shadow-sm h-100">
 <Card.Body>
 <h6 className="fw-bold mb-3">Manpower Used</h6>

 {dpr.manpower ? (
 <ul className="list-group small">
 <li className="list-group-item d-flex justify-content-between">
 <span>Supervisors</span>
 <strong>{dpr.manpower.supervisors}</strong>
 </li>
 <li className="list-group-item d-flex justify-content-between">
 <span>Engineers</span>
 <strong>{dpr.manpower.engineers}</strong>
 </li>
 <li className="list-group-item d-flex justify-content-between">
 <span>Skilled</span>
 <strong>{dpr.manpower.skilled}</strong>
 </li>
 <li className="list-group-item d-flex justify-content-between">
 <span>Unskilled</span>
 <strong>{dpr.manpower.unskilled}</strong>
 </li>
 <li className="list-group-item d-flex justify-content-between bg-light">
 <strong>Total</strong>
 <strong>{dpr.manpower.total}</strong>
 </li>
 </ul>
 ) : (
 <p className="text-muted">No manpower data.</p>
 )}
 </Card.Body>
 </Card>
 </Col>

 {/* ISSUES */}
 <Col md={6}>
 <Card className="border-0 shadow-sm h-100">
 <Card.Body>
 <h6 className="fw-bold mb-3">Issues</h6>

 {dpr.issuesRaised && dpr.issueDetails?.length > 0 ? (
 dpr.issueDetails.map((issue, idx) => (
 <div
 key={idx}
 className="p-3 mb-2 border rounded bg-light"
 >
 <strong>{issue.issue}</strong>
 <div className="mt-2">
 <Badge className="rounded-pill me-2" bg="danger">
 {issue.severity}
 </Badge>
 <Badge className="rounded-pill" bg="secondary">{issue.status}</Badge>
 </div>
 </div>
 ))
 ) : (
 <p className="text-muted">No issues recorded.</p>
 )}
 </Card.Body>
 </Card>
 </Col>

 {/* NEXT DAY PLAN + WEATHER */}
 <Col md={6}>
 <Card className="border-0 shadow-sm h-100">
 <Card.Body>
 <h6 className="fw-bold mb-3">Next Day Plan</h6>
 {dpr.nextDayPlan ? (
   <div
     className="rich-text-content"
     dangerouslySetInnerHTML={{ __html: dpr.nextDayPlan }}
   />
 ) : (
   <p className="text-muted">No plan added.</p>
 )}

 <h6 className="fw-bold mt-4 mb-2">Weather</h6>
 <p>{dpr.weather || "Not recorded."}</p>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Card.Body>
 </Card>
 ))
 )}
 </>
 );
};

export default DPRTab;
